import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  QrCode,
  Home,
  User,
  Gift,
  Wallet,
  Camera,
  Keyboard,
  Star,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowRight
} from 'lucide-react';
import ClientQRScanner from '../Components/ClientQRScanner';
import {
  addPointsFromQR,
  addPointsManually,
  clearMessage,
  loadClient
} from '../Redux/Actions/ClientActions';
import './MyPoints.css';
import './ScanPoints.css';
import './ScanPoints_ErrorPopup.css';

const ScanPoints = () => {
  const [showScanner, setShowScanner] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [scanError, setScanError] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { points, message, error, loading: pointsLoading } = useSelector(state => state.clientReducer);
  const loading = pointsLoading || isProcessing;

  // Clear Redux messages only (NOT scanError - it's manual only)
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        dispatch(clearMessage());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error, dispatch]);

  /**
   * Enrichit les données du scan pour s'assurer qu'elles respectent
   * la logique d'ajout de points originale (KDOxxx, Numéro de facture, etc.)
   */
  const enrichScanData = (data) => {
    let enriched = { ...data };
    const rawValue = (data.code || data.rawData || "").toString().trim();

    // 1. Détection des codes KdoXXX
    if (rawValue.toUpperCase().startsWith('KDO')) {
      const pointsMatch = rawValue.match(/KDO(\d+)/i);
      enriched.type = 'SPINTOSS_POINTS';
      if (pointsMatch) {
        enriched.points = parseInt(pointsMatch[1]);
      }
    }
    // 2. Détection des numéros de facture simples (ex: "001", "123")
    else if (/^\d{1,5}$/.test(rawValue)) {
      enriched.type = 'BILL_NUMBER';
      enriched.billNumber = rawValue.padStart(3, '0');
    }

    return enriched;
  };

  const handleScanSuccess = async (qrData) => {
    setIsProcessing(true);
    setScanError(null);

    try {
      // PRESERVE LOGIC: Enrichir les données avant l'envoi
      const finalData = enrichScanData(qrData);

      const result = await dispatch(addPointsFromQR(finalData));

      console.log('📋 Résultat du scan:', result);

      if (result && result.success) {
        // Recharger les données du client pour forcer le rafraîchissement
        await dispatch(loadClient());

        setShowScanner(false);
        setScanError(null);
      } else {
        // Fermer le scanner et afficher l'erreur comme popup
        setShowScanner(false);
        const errorMsg = result?.message || 'Erreur lors de l\'ajout des points';
        setScanError(errorMsg);
        // Clear Redux error pour éviter les interférences
        setTimeout(() => dispatch(clearMessage()), 100);
      }
    } catch (err) {
      setShowScanner(false);
      setScanError(err.message);
      // Clear Redux error pour éviter les interférences
      setTimeout(() => dispatch(clearMessage()), 100);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;

    setIsProcessing(true);
    setScanError(null);

    try {
      // Utilise la même logique d'enrichissement que le scan
      const manualData = enrichScanData({ code: manualCode.trim(), rawData: manualCode.trim() });
      const result = await dispatch(addPointsFromQR(manualData));

      if (result && result.success) {
        setManualCode('');
      } else {
        throw new Error(result?.message || 'Code invalide');
      }
    } catch (err) {
      setScanError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="mobile-app-container">
      {/* Immersive Processing Overlay */}
      {(isProcessing || pointsLoading) && (
        <div className="mobile-processing-overlay">
          <div className="native-spinner"></div>
          <p className="font-semibold text-primary">Vérification en cours...</p>
        </div>
      )}

      {/* Persistent Native Header */}
      <header className="mobile-native-header">
        <div className="header-main-content">
          <div className="header-text-group">
            <h1 className="header-title-premium">Scanner</h1>
          </div>
          <div className="header-points-badge" onClick={() => navigate('/history')}>
            <Star size={16} className="fill-white" />
            <span className="points-amount">{points?.toLocaleString() || 0}</span>
          </div>
        </div>
      </header>

      <main className="mobile-main-feed animate-fade-in">
        {/* Alerts / Feedback */}
        {message && (
          <div className="mobile-message-banner success">
            <CheckCircle2 size={20} />
            <span>{message.text || "Points ajoutés !"}</span>
          </div>
        )}
        {error && (
          <div className="mobile-message-banner error">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* Hero Scan Card */}
        <div className="scan-hero-card">
          <div className="hero-icon-wrapper">
            <Camera size={40} strokeWidth={2.5} />
          </div>
          <h2 className="hero-title">Scannez votre ticket</h2>
          <p className="hero-subtitle">
            Capturez le QR Code de votre addition <br /> pour recevoir vos points cadeaux.
          </p>
          <button
            className="btn-start-scan"
            onClick={() => setShowScanner(true)}
            disabled={loading}
          >
            <QrCode size={20} />
            Lancer le scanner
          </button>
        </div>

        {/* Manual Keyboard Section */}
        <div className="manual-entry-section">
          <div className="section-label">
            <Keyboard size={18} />
            <span>Saisie manuelle</span>
          </div>

          <form onSubmit={handleManualSubmit} className="manual-form">
            <div className="input-group">
              <input
                type="text"
                className="manual-input"
                placeholder="Ex: KDO100 ou N° de facture"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                disabled={loading}
              />
              <ArrowRight
                className={`input-icon-left ${manualCode ? 'text-primary' : 'text-muted'}`}
                size={20}
              />
            </div>

            <button
              type="submit"
              className="btn-submit-manual"
              disabled={loading || !manualCode.trim()}
            >
              {loading ? <Loader2 className="animate-spin mx-auto" size={20} /> : "Valider le code"}
            </button>
          </form>
        </div>
      </main>

      {/* Full-screen Native Scanner Modal (Framed) */}
      {showScanner && (
        <div className="scanner-modal-overlay">
          <ClientQRScanner
            onScanSuccess={handleScanSuccess}
            onScanError={(err) => {
              setShowScanner(false);
              setScanError(err.message);
            }}
            onHide={() => {
              setShowScanner(false);
              // NE PAS clear scanError ici - il sera cleared manuellement par l'utilisateur via le popup
            }}
          />
        </div>
      )}

      {/* Full-Screen Error Popup (Replaces Camera Position) */}
      {scanError && !showScanner && (
        <div className="error-popup-overlay">
          <div className="error-popup-content">
            <button
              className="error-popup-x-close"
              onClick={() => setScanError(null)}
              aria-label="Fermer"
            >
              ✕
            </button>
            <div className="error-icon-wrapper">
              <AlertCircle size={64} strokeWidth={2} />
            </div>
            <h3 className="error-popup-title">Oups !</h3>
            <p className="error-popup-message">{scanError}</p>
            <button
              className="error-popup-close-btn"
              onClick={() => setScanError(null)}
            >
              J'ai compris
            </button>
          </div>
        </div>
      )}

      {/* Shared Floating Bottom Nav */}
      <nav className="mobile-floating-nav">
        <button className="nav-btn" onClick={() => navigate('/my-points')}>
          <Home size={22} />
          <span>Accueil</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/rewards')}>
          <Gift size={22} />
          <span>Cadeaux</span>
        </button>
        <button className="nav-btn is-active">
          <div className="nav-center-scan">
            <QrCode size={24} color="white" />
          </div>
        </button>
        <button className="nav-btn" onClick={() => navigate('/wallet')}>
          <Wallet size={22} />
          <span>Wallet</span>
        </button>
        <button className="nav-btn" onClick={() => navigate('/profile')}>
          <User size={22} />
          <span>Profil</span>
        </button>
      </nav>
    </div>
  );
};

export default ScanPoints;